import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, MessageCircle, CheckCircle, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import EditRideModal from './EditRideModal';

export default function MyRides() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRide, setEditingRide] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyRides();
  }, []);

  const fetchMyRides = async () => {
    try {
      const response = await axios.get('/rides/my-rides');
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRide = async (rideId) => {
    try {
      await axios.post(`/rides/${rideId}/complete`);
      toast.success('Ride marked as completed');
      fetchMyRides();
    } catch (error) {
      toast.error('Failed to complete ride');
    }
  };

  const getLocationDisplay = (ride) => {
    if (ride.formatted_address) {
      // Show first part of address (city/area)
      return ride.formatted_address.split(',').slice(0, 2).join(', ');
    }
    return ride.location || 'Location not set';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <p className="text-gray-400">Loading your rides...</p>
      </div>
    );
  }

  if (rides.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400 text-lg mb-4">You haven't created any rides yet</p>
        <p className="text-gray-500 text-sm">Click "Create" to get started!</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="my-rides">
        {rides.map((ride) => (
          <Card key={ride.id} data-testid="my-ride-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg" data-testid="my-ride-distance">{ride.distance}km Ride</CardTitle>
                  {ride.category && (
                    <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-400 text-xs mt-1">
                      {ride.category === 'road' && 'Road'}
                      {ride.category === 'mountain' && 'Mountain'}
                      {ride.category === 'gravel' && 'Gravel'}
                      {ride.category === 'commute' && 'Commute'}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {ride.status === 'active' && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setEditingRide(ride)}
                      title="Edit ride"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'} data-testid="my-ride-status">
                    {ride.status}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-purple-500" />
                <span className="text-muted-foreground" data-testid="my-ride-datetime">{ride.date} at {ride.time}</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground line-clamp-2" data-testid="my-ride-location">
                  {getLocationDisplay(ride)}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-purple-500" />
                <span className="text-muted-foreground" data-testid="my-ride-participants">
                  {ride.participants_count} rider{ride.participants_count !== 1 ? 's' : ''} joined
                </span>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/ride/${ride.id}`)}
                  data-testid="view-my-ride-button"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  View
                </Button>
                {ride.status === 'active' && (
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleCompleteRide(ride.id)}
                    data-testid="complete-ride-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Complete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Ride Modal */}
      {editingRide && (
        <EditRideModal
          ride={editingRide}
          onClose={() => setEditingRide(null)}
          onUpdated={() => {
            setEditingRide(null);
            fetchMyRides();
          }}
        />
      )}
    </>
  );
}
